import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import { Menu, PanelLeftIcon } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ShortcutHint } from '@/components/ui/shortcut-hint'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIsMobile } from '@/hooks/use-mobile'
import { holdsKeyboardFocus } from '@/lib/keyboard'
import { isInOverlayLayer } from '@/lib/overlayLayers'
import { SHORTCUT_REGISTRY } from '@/lib/shortcutRegistry'
import { shortcut } from '@/lib/shortcuts'
import { useShortcut } from '@/lib/useShortcut'
import { cn } from '@/lib/utils'
import { SidebarContext, type SidebarContextProps, useSidebar } from './sidebar-context'

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY_OPEN = 'sidebarOpen'
const STORAGE_KEY_WIDTH = 'sidebarWidth'

const SIDEBAR_WIDTH_MOBILE = '18rem'

const SIDEBAR_MIN_WIDTH = 220
const SIDEBAR_MAX_WIDTH = 420
const SIDEBAR_DEFAULT_WIDTH = 260

/** React bubbles events from portaled menus and dialogs through the panel; only its own DOM may pin it. */
const isOnPanel = (e: React.SyntheticEvent) => e.currentTarget.contains(e.target as Node)

const SLIDE = 'transform 250ms ease-out'
const FLOAT_IN = 'transform 340ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 160ms ease'
const FLOAT_OUT = 'transform 240ms cubic-bezier(0.4, 0, 0.6, 1), opacity 200ms ease'

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

function SidebarProvider({ className, style, children, ...props }: React.ComponentProps<'div'>) {
	const isMobile = useIsMobile()
	const [openMobile, setOpenMobile] = React.useState(false)

	const [isOpen, setIsOpenInternal] = React.useState<boolean>(() => {
		const stored = localStorage.getItem(STORAGE_KEY_OPEN)
		return stored !== null ? stored === 'true' : true
	})

	const setIsOpen = React.useCallback((value: boolean) => {
		setIsOpenInternal(value)
		localStorage.setItem(STORAGE_KEY_OPEN, String(value))
	}, [])

	const [width, setWidthInternal] = React.useState<number>(() => {
		const stored = localStorage.getItem(STORAGE_KEY_WIDTH)
		const parsed = stored ? Number.parseInt(stored, 10) : SIDEBAR_DEFAULT_WIDTH
		return Number.isNaN(parsed)
			? SIDEBAR_DEFAULT_WIDTH
			: Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, parsed))
	})

	const setWidth = React.useCallback((w: number) => {
		const clamped = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, w))
		setWidthInternal(clamped)
		localStorage.setItem(STORAGE_KEY_WIDTH, String(clamped))
	}, [])

	const [isFloating, setIsFloating] = React.useState(false)

	const openSidebar = React.useCallback(() => {
		setIsFloating(false)
		setIsOpen(true)
	}, [setIsOpen])

	const closeSidebar = React.useCallback(() => {
		setIsOpen(false)
	}, [setIsOpen])

	const toggleSidebar = React.useCallback(() => {
		if (isMobile) {
			setOpenMobile(v => !v)
		} else if (isOpen) {
			closeSidebar()
		} else {
			openSidebar()
		}
	}, [isMobile, isOpen, openSidebar, closeSidebar])

	const onToggleSidebar = React.useCallback(
		(e: KeyboardEvent) => {
			e.preventDefault()
			toggleSidebar()
		},
		[toggleSidebar],
	)
	useShortcut('sidebar.toggle', onToggleSidebar, { skipInOverlay: true })
	useShortcut('sidebar.toggleAlt', onToggleSidebar, { skipInOverlay: true })

	const contextValue = React.useMemo<SidebarContextProps>(
		() => ({
			isOpen,
			width,
			isFloating,
			toggleSidebar,
			openSidebar,
			closeSidebar,
			setWidth,
			setIsFloating,
			isMobile,
			openMobile,
			setOpenMobile,
		}),
		[isOpen, width, isFloating, toggleSidebar, openSidebar, closeSidebar, setWidth, isMobile, openMobile],
	)

	return (
		<SidebarContext.Provider value={contextValue}>
			<div data-slot='sidebar-wrapper' className={cn('flex h-svh w-full', className)} style={style} {...props}>
				{children}
			</div>
		</SidebarContext.Provider>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar
// ─────────────────────────────────────────────────────────────────────────────

function Sidebar({
	side = 'left',
	className,
	children,
	...props
}: React.ComponentProps<'div'> & {
	side?: 'left' | 'right'
}) {
	const {
		isMobile,
		isOpen,
		width,
		setWidth,
		closeSidebar,
		openSidebar,
		isFloating,
		setIsFloating,
		openMobile,
		setOpenMobile,
	} = useSidebar()

	const isDragging = React.useRef(false)
	const dragStartX = React.useRef(0)
	const dragStartWidth = React.useRef(width)
	const [suppressTransition, setSuppressTransition] = React.useState(false)

	const onResizeMouseDown = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			isDragging.current = true
			dragStartX.current = e.clientX
			dragStartWidth.current = width
			setSuppressTransition(true)
			document.body.style.cursor = 'col-resize'
			document.body.style.userSelect = 'none'

			const onMouseMove = (me: MouseEvent) => {
				if (!isDragging.current) {
					return
				}
				const newWidth = dragStartWidth.current + (me.clientX - dragStartX.current)
				if (newWidth < SIDEBAR_MIN_WIDTH) {
					closeSidebar()
					cleanup()
				} else {
					setWidth(newWidth)
				}
			}

			const cleanup = () => {
				isDragging.current = false
				document.body.style.cursor = ''
				document.body.style.userSelect = ''
				setSuppressTransition(false)
				document.removeEventListener('mousemove', onMouseMove)
				document.removeEventListener('mouseup', onMouseUp)
			}

			const onMouseUp = () => {
				cleanup()
			}

			document.addEventListener('mousemove', onMouseMove)
			document.addEventListener('mouseup', onMouseUp)
		},
		[width, setWidth, closeSidebar],
	)

	const hoverZoneRef = React.useRef<HTMLDivElement>(null)
	const panelRef = React.useRef<HTMLElement>(null)

	const handleEdgeEnter = React.useCallback(() => {
		if (!isOpen) {
			setIsFloating(true)
		}
	}, [isOpen, setIsFloating])

	const handleFloatLeave = React.useCallback(
		(e: React.MouseEvent) => {
			const related = e.relatedTarget as Node | null
			const panel = panelRef.current
			// A keyboard user working inside the float keeps it; closing would make it inert and drop their focus.
			// Focus a click left behind does not count, or a clicked link would pin the float open.
			const keyboardInside = panel?.contains(document.activeElement) && holdsKeyboardFocus(document.activeElement)
			if (panel?.contains(related) || hoverZoneRef.current?.contains(related) || keyboardInside) {
				return
			}
			setIsFloating(false)
		},
		[setIsFloating],
	)

	const handleFloatBlur = React.useCallback(
		(e: React.FocusEvent) => {
			const panel = panelRef.current
			const next = e.relatedTarget as Node | null
			// Focus moving into a menu or dialog the panel opened stays with the panel.
			if (panel?.contains(next) || isInOverlayLayer(next) || panel?.matches(':hover')) {
				return
			}
			setIsFloating(false)
		},
		[setIsFloating],
	)

	const handleFloatClick = React.useCallback(
		(e: React.MouseEvent) => {
			const target = e.target as HTMLElement
			if (isOnPanel(e) && !target.closest('a, button, [role="button"]')) {
				openSidebar()
			}
		},
		[openSidebar],
	)

	const handleFloatKeyDown = React.useCallback(
		(e: React.KeyboardEvent) => {
			if (isOnPanel(e) && (e.key === 'Enter' || e.key === ' ')) {
				openSidebar()
			}
		},
		[openSidebar],
	)

	if (isMobile) {
		return (
			<Sheet open={openMobile} onOpenChange={setOpenMobile}>
				<SheetContent
					data-sidebar='sidebar'
					data-slot='sidebar'
					data-mobile='true'
					className='bg-sidebar text-sidebar-foreground p-0 [&>button]:hidden'
					style={{ '--sidebar-width': SIDEBAR_WIDTH_MOBILE } as React.CSSProperties}
					side={side}>
					<SheetHeader className='sr-only'>
						<SheetTitle>Sidebar</SheetTitle>
						<SheetDescription>Displays the mobile sidebar.</SheetDescription>
					</SheetHeader>
					<div className='flex h-full w-full flex-col'>{children}</div>
				</SheetContent>
			</Sheet>
		)
	}

	// One panel morphs between flush and floating, so its children (menus, dialogs, listeners) never mount twice.
	const isShown = isOpen || isFloating
	const transition = suppressTransition ? 'none' : isOpen ? SLIDE : isFloating ? FLOAT_IN : FLOAT_OUT

	return (
		<>
			<aside
				ref={panelRef}
				data-slot='sidebar'
				data-state={isOpen ? 'expanded' : 'collapsed'}
				aria-label={isOpen ? undefined : 'Sidebar preview'}
				inert={!isShown}
				onMouseLeave={isOpen ? undefined : handleFloatLeave}
				onBlur={isOpen ? undefined : handleFloatBlur}
				onClick={isOpen ? undefined : handleFloatClick}
				onKeyDown={isOpen ? undefined : handleFloatKeyDown}
				className={cn(
					'fixed flex flex-col bg-sidebar text-sidebar-foreground',
					isOpen
						? 'inset-y-0 left-0 z-20 h-svh'
						: 'left-2 z-30 rounded-md border border-sidebar-border shadow-levitating cursor-pointer',
					className,
				)}
				style={{
					...(!isOpen && { top: '3rem', bottom: 'var(--sidebar-preview-bottom, 0px)' }),
					width: `${width}px`,
					transform: isShown ? 'translateX(0)' : 'translateX(calc(-100% - 0.75rem))',
					opacity: isShown ? 1 : 0,
					pointerEvents: isShown ? 'auto' : 'none',
					transition,
					willChange: 'transform',
				}}
				{...props}>
				<div
					data-sidebar='sidebar'
					data-slot='sidebar-inner'
					className={cn(
						'flex h-full w-full flex-col overflow-hidden',
						isOpen ? 'border-r border-sidebar-border' : 'rounded-md',
					)}>
					{children}
				</div>

				{isOpen && (
					<div
						data-sidebar='resize-handle'
						onMouseDown={onResizeMouseDown}
						style={{
							position: 'absolute',
							top: 0,
							right: 0,
							width: '4px',
							height: '100%',
							cursor: 'col-resize',
							zIndex: 30,
						}}
					/>
				)}
			</aside>

			{!isOpen && (
				<div
					ref={hoverZoneRef}
					data-sidebar='edge-zone'
					data-testid='sidebar-edge-zone'
					onMouseEnter={handleEdgeEnter}
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '4px',
						height: '100dvh',
						zIndex: 25,
					}}
				/>
			)}
		</>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// SidebarTrigger — hidden when expanded (collapse is via drag), visible when collapsed
// ─────────────────────────────────────────────────────────────────────────────

function SidebarTrigger({ className, onClick, ...props }: React.ComponentProps<typeof Button>) {
	const { toggleSidebar, isOpen, isMobile } = useSidebar()

	if (isOpen && !isMobile) {
		return null
	}

	const label = isMobile ? 'Toggle sidebar' : 'Expand sidebar'
	const button = (
		<Button
			data-sidebar='trigger'
			data-slot='sidebar-trigger'
			variant='ghost'
			size='icon'
			className={cn('size-control', className)}
			onClick={event => {
				onClick?.(event)
				toggleSidebar()
			}}
			{...props}>
			{isMobile ? <Menu className='size-(--icon-standalone)' /> : <PanelLeftIcon />}
			<span className='sr-only'>{label}</span>
		</Button>
	)

	// Mobile has no keyboard, so the hint only earns its place on desktop.
	if (isMobile) {
		return button
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>{button}</TooltipTrigger>
			<TooltipContent side='right'>
				<ShortcutHint label={label} keys={shortcut(SHORTCUT_REGISTRY['sidebar.toggle'].keys)} />
			</TooltipContent>
		</Tooltip>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// SidebarRail — no-op, kept for API compat
// ─────────────────────────────────────────────────────────────────────────────

function SidebarRail(props: React.ComponentProps<'button'>) {
	void props
	return null
}

// ─────────────────────────────────────────────────────────────────────────────
// SidebarInset — content area that adjusts margin-left to match sidebar
// ─────────────────────────────────────────────────────────────────────────────

function SidebarInset({ className, style, ...props }: React.ComponentProps<'main'>) {
	const { isOpen, width, isMobile } = useSidebar()

	return (
		<main
			data-slot='sidebar-inset'
			className={cn('bg-background relative flex w-full flex-1 flex-col min-w-0', className)}
			style={{
				marginLeft: isOpen && !isMobile ? `${width}px` : '0px',
				transition: 'margin-left 250ms ease-out',
				...style,
			}}
			{...props}
		/>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components (unchanged from original shadcn)
// ─────────────────────────────────────────────────────────────────────────────

function SidebarInput({ className, ...props }: React.ComponentProps<typeof Input>) {
	return (
		<Input
			data-slot='sidebar-input'
			data-sidebar='input'
			className={cn('bg-background h-8 w-full shadow-none', className)}
			{...props}
		/>
	)
}

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div data-slot='sidebar-header' data-sidebar='header' className={cn('flex flex-col gap-2', className)} {...props} />
	)
}

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='sidebar-footer'
			data-sidebar='footer'
			className={cn('flex flex-col gap-2 p-2', className)}
			{...props}
		/>
	)
}

function SidebarSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
	return (
		<Separator
			data-slot='sidebar-separator'
			data-sidebar='separator'
			className={cn('bg-sidebar-border mx-2 w-auto', className)}
			{...props}
		/>
	)
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='sidebar-content'
			data-sidebar='content'
			className={cn('flex min-h-0 flex-1 flex-col gap-2 overflow-auto', className)}
			{...props}
		/>
	)
}

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='sidebar-group'
			data-sidebar='group'
			className={cn('relative flex w-full min-w-0 flex-col px-2', className)}
			{...props}
		/>
	)
}

function SidebarGroupLabel({
	className,
	asChild = false,
	...props
}: React.ComponentProps<'div'> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : 'div'
	return (
		<Comp
			data-slot='sidebar-group-label'
			data-sidebar='group-label'
			className={cn(
				'text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-body font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-[14px] [&>svg]:shrink-0',
				className,
			)}
			{...props}
		/>
	)
}

function SidebarGroupAction({
	className,
	asChild = false,
	...props
}: React.ComponentProps<'button'> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot='sidebar-group-action'
			data-sidebar='group-action'
			className={cn(
				'text-sidebar-foreground ring-sidebar-ring data-[active=true]:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-[14px] [&>svg]:shrink-0',
				'after:absolute after:-inset-2 md:after:hidden',
				className,
			)}
			{...props}
		/>
	)
}

function SidebarGroupContent({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='sidebar-group-content'
			data-sidebar='group-content'
			className={cn('w-full text-body', className)}
			{...props}
		/>
	)
}

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
	return (
		<ul
			data-slot='sidebar-menu'
			data-sidebar='menu'
			className={cn('flex w-full min-w-0 flex-col gap-1', className)}
			{...props}
		/>
	)
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
	return (
		<li
			data-slot='sidebar-menu-item'
			data-sidebar='menu-item'
			className={cn('group/menu-item relative', className)}
			{...props}
		/>
	)
}

const sidebarMenuButtonVariants = cva(
	'peer/menu-button flex w-full items-center gap-icon-text! overflow-hidden rounded-md p-2 text-left text-body outline-hidden ring-sidebar-ring transition-[width,height,padding,background-color] data-[active=true]:font-medium data-[active=true]:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[active=true]:hover:bg-sidebar-accent-hover data-[state=open]:hover:text-sidebar-accent-foreground [&>span:last-child]:truncate [&>svg]:size-[14px] [&>svg]:shrink-0 cursor-pointer',
	{
		variants: {
			variant: {
				default: 'group-data-[active=true]:font-medium active:bg-sidebar-accent hover:text-sidebar-accent-foreground',
				outline:
					'bg-background shadow-[0_0_0_1px_var(--sidebar-border)] hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_var(--sidebar-accent)]',
			},
			size: {
				default: 'h-control text-body',
				sm: 'h-control text-body',
				lg: 'h-12 text-body',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

function SidebarMenuButton({
	asChild = false,
	isActive = false,
	variant = 'default',
	size = 'default',
	tooltip,
	className,
	...props
}: React.ComponentProps<'button'> & {
	asChild?: boolean
	isActive?: boolean
	tooltip?: string | React.ComponentProps<typeof TooltipContent>
} & VariantProps<typeof sidebarMenuButtonVariants>) {
	const Comp = asChild ? Slot : 'button'

	const button = (
		<Comp
			data-slot='sidebar-menu-button'
			data-sidebar='menu-button'
			data-size={size}
			data-active={isActive}
			className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
			{...props}
		/>
	)

	if (!tooltip) {
		return button
	}

	if (typeof tooltip === 'string') {
		tooltip = { children: tooltip }
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>{button}</TooltipTrigger>
			<TooltipContent side='right' align='center' {...tooltip} />
		</Tooltip>
	)
}

function SidebarMenuAction({
	className,
	asChild = false,
	showOnHover = false,
	...props
}: React.ComponentProps<'button'> & {
	asChild?: boolean
	showOnHover?: boolean
}) {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot='sidebar-menu-action'
			data-sidebar='menu-action'
			className={cn(
				'text-sidebar-foreground ring-sidebar-ring data-[active=true]:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-[14px] [&>svg]:shrink-0',
				'after:absolute after:-inset-2 md:after:hidden',
				'peer-data-[size=sm]/menu-button:top-1',
				'peer-data-[size=default]/menu-button:top-1.5',
				'peer-data-[size=lg]/menu-button:top-2.5',
				showOnHover &&
					'peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0',
				className,
			)}
			{...props}
		/>
	)
}

function SidebarMenuBadge({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='sidebar-menu-badge'
			data-sidebar='menu-badge'
			className={cn(
				'text-sidebar-foreground pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-body font-medium tabular-nums select-none',
				'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
				'peer-data-[size=sm]/menu-button:top-1',
				'peer-data-[size=default]/menu-button:top-1.5',
				'peer-data-[size=lg]/menu-button:top-2.5',
				className,
			)}
			{...props}
		/>
	)
}

function SidebarMenuSkeleton({
	className,
	showIcon = false,
	...props
}: React.ComponentProps<'div'> & { showIcon?: boolean }) {
	const skeletonWidth = React.useMemo(() => `${Math.floor(Math.random() * 40) + 50}%`, [])
	return (
		<div
			data-slot='sidebar-menu-skeleton'
			data-sidebar='menu-skeleton'
			className={cn('flex h-8 items-center gap-icon-text! rounded-md px-2', className)}
			{...props}>
			{showIcon && <Skeleton className='size-[14px] rounded-md' data-sidebar='menu-skeleton-icon' />}
			<Skeleton
				className='h-4 max-w-(--skeleton-width) flex-1'
				data-sidebar='menu-skeleton-text'
				style={{ '--skeleton-width': skeletonWidth } as React.CSSProperties}
			/>
		</div>
	)
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
	return (
		<ul
			data-slot='sidebar-menu-sub'
			data-sidebar='menu-sub'
			className={cn(
				'border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l px-1 py-0.5',
				className,
			)}
			{...props}
		/>
	)
}

function SidebarMenuSubItem({ className, ...props }: React.ComponentProps<'li'>) {
	return (
		<li
			data-slot='sidebar-menu-sub-item'
			data-sidebar='menu-sub-item'
			className={cn('group/menu-sub-item relative', className)}
			{...props}
		/>
	)
}

function SidebarMenuSubButton({
	asChild = false,
	size = 'md',
	isActive = false,
	className,
	...props
}: React.ComponentProps<'a'> & {
	asChild?: boolean
	size?: 'sm' | 'md'
	isActive?: boolean
}) {
	const Comp = asChild ? Slot : 'a'
	return (
		<Comp
			data-slot='sidebar-menu-sub-button'
			data-sidebar='menu-sub-button'
			data-size={size}
			data-active={isActive}
			className={cn(
				'text-sidebar-foreground ring-sidebar-ring hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-control min-w-0 -translate-x-px items-center gap-icon-text! overflow-hidden rounded-md outline-hidden focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-[14px] [&>svg]:shrink-0',
				'data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[active=true]:hover:bg-sidebar-accent-hover',
				size === 'sm' && 'text-body',
				size === 'md' && 'text-body',
				className,
			)}
			{...props}
		/>
	)
}

export {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupAction,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarInput,
	SidebarInset,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuBadge,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSkeleton,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarProvider,
	SidebarRail,
	SidebarSeparator,
	SidebarTrigger,
	useSidebar,
}
